import React, { useState, useEffect, useRef } from 'react';
import TomSelect from '../../components/common/TomSelect';
import CompetencyHistory from '../../components/audit/CompetencyHistory';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';

// Format date for display
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB');
};

// Format status for display
const mapStatusDisplay = (status) => {
  const statusMap = {
    'pending': 'Pending Approval',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'revision_requested': 'Need Revision',
    'need_revision': 'Need Revision'
  };
  return statusMap[status] || status;
};

const mapStatusToDecision = (status) => {
  switch (status) {
    case 'approved':
      return 'approve';
    case 'rejected':
      return 'reject';
    case 'revision_requested':
    case 'need_revision':
      return 'revision';
    default:
      return 'approve';
  }
};

const statusBadge = (status) => {
  switch (status) {
    case 'approved':
    case 'Approved':
      return <span className="badge bg-success text-light">{status}</span>;
    case 'rejected':
    case 'Rejected':
      return <span className="badge bg-danger text-light">{status}</span>;
    case 'pending':
    case 'Pending Approval':
      return <span className="badge bg-warning text-light">{status}</span>;
    case 'revision_requested':
    case 'Revision Requested':
    case 'need_revision':
    case 'Need Revision':
      return <span className="badge bg-info text-light">{status}</span>;
    default:
      return <span className="badge bg-secondary">{status}</span>;
  }
};

const normalizeCompetencyStatus = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('pending')) return 'pending';
  if (value.includes('approved')) return 'approved';
  if (value.includes('rejected')) return 'rejected';
  if (value.includes('revision')) return 'need_revision';
  return value;
};

const isEditableCompetencyStatus = (status) => {
  const normalizedStatus = normalizeCompetencyStatus(status);
  return normalizedStatus === 'pending' || normalizedStatus === 'need_revision';
};

const createEmptyStandardItem = () => ({
  standard: '',
  sectorCodes: [],
  sectorIds: [],
  documentFile: null,
  documentPath: '',
  documentName: '',
  uid: `${Date.now()}-${Math.random()}`
});

const isRowActive = (row) => row?.isActive === undefined || row?.isActive === null || row.isActive === true;

const Competency = () => {
  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canCompetency = (key) => can(key, 'competency');

  const modalKeyRef = useRef(0);

  // Active/inactive toggle state
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateNotes, setDeactivateNotes] = useState('');
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  
  // Form state
  const [employee, setEmployee] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [selectedInternalUser, setSelectedInternalUser] = useState(null);
  const [standard, setStandard] = useState('');
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [justification, setJustification] = useState('');
  const [standards, setStandards] = useState([]);
  const [standardOptions, setStandardOptions] = useState([]);
  const [standardSearchQuery, setStandardSearchQuery] = useState('');
  const [qualificationId, setQualificationId] = useState('');
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [requestType, setRequestType] = useState('New Request'); // "New Request" or "Upgrade"
  const [currentType, setCurrentType] = useState(''); // TomSelect value (qualificationId)
  const [currentTypeLevel, setCurrentTypeLevel] = useState(0); // Track level untuk validasi
  const [requestedType, setRequestedType] = useState(''); // TomSelect value (qualificationId)
  const [approvedQualifications, setApprovedQualifications] = useState([]);
  const [availableLevelsForUpgrade, setAvailableLevelsForUpgrade] = useState([]);
  
  // Table and list state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const STANDARD_COLLAPSE_LIMIT = 5;

  const toggleGroupExpanded = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Review modal state
  const [reviewData, setReviewData] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDecision, setReviewDecision] = useState('approve');
  const [reviewMode, setReviewMode] = useState('edit');
  
  // File preview modal state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const getEmployeeDisplayNameById = (employeeIdValue) => {
    const id = String(employeeIdValue || '').trim();
    if (!id) return '';
    const user = internalUsers.find(u =>
      String(u.employeeId || '').trim() === id || String(u.id || '').trim() === id
    );
    if (!user) return id;
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || id;
  };

  const findInternalUserForRow = (row, users) => {
    if (!row || !Array.isArray(users) || users.length === 0) return null;
    
    // 🔑 PRIMARY: Try to match by user_id (from row.user_id from API)
    if (row.user_id) {
      const user = users.find(u => String(u.id || '').trim() === String(row.user_id || '').trim());
      if (user) {
        console.log('✅ Found user by user_id:', {rowUserId: row.user_id, userId: user.id, employeeId: user.employeeId });
        return user;
      }
    }
    
    // FALLBACK: Try to match by employeeId
    const rowEmployeeId = String(row.employeeId || '').trim();
    if (rowEmployeeId) {
      let user = users.find(u => String(u.employeeId || '').trim() === rowEmployeeId);
      if (!user) {
        user = users.find(u => String(u.id || '').trim() === rowEmployeeId);
      }
      if (user) {
        console.log('⚠️  Matched user by employeeId (fallback):', {rowEmployeeId, userId: user.id, user_id: user.id});
        return user;
      }
    }

    console.warn('⛔ Could not find user for row:', {rowUserId: row.user_id, rowEmployeeId: row.employeeId});
    return null;
  };

  const getSelectedEmployeeRequestId = () => String(
    selectedInternalUser?.id ||
    selectedInternalUser?.user_id ||
    employeeId ||
    selectedInternalUser?.employeeId ||
    ''
  ).trim();

  const handleDownloadPreview = async () => {
    if (!previewFile) return;
    const filename = previewFileName || 'document';
    try {
      if (String(previewFile).startsWith('data:')) {
        const a = document.createElement('a');
        a.href = previewFile;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      const path = String(previewFile || '').trim();
      const requestPath = path.startsWith('http')
        ? path
        : (path.startsWith('/') ? path : `/${path}`);
      const response = await api.get(requestPath, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
      alert('Failed to download file');
    }
  };

  // Fetch sectors
  useEffect(() => {
    let mounted = true;
    const fetchSectors = async () => {
      try {
        const res = await api.get('/md/sector');
        if (mounted && res && res.data) {
          setSectors(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch sectors:', err);
      }
    };
    fetchSectors();
    return () => { mounted = false; };
  }, []);

  // Fetch standards
  useEffect(() => {
    let mounted = true;
    const fetchStandards = async () => {
      try {
        const res = await api.get('/md/standards/page', { params: { page: 0, size: 1000 } });
        if (mounted && res && res.data && res.data.content) {
          setStandardOptions(res.data.content);
        }
      } catch (err) {
        console.error('Failed to fetch standards:', err);
      }
    };
    fetchStandards();
    return () => { mounted = false; };
  }, []);

  // Fetch internal users
  useEffect(() => {
    let mounted = true;
    const fetchInternalUsers = async () => {
      try {
        const res = await api.get('/md/internal-users?page=0&size=1000');
        if (mounted && res && res.data && res.data.content) {
          setInternalUsers(res.data.content);
        }
      } catch (err) {
        console.error('Failed to fetch internal users:', err);
      }
    };
    fetchInternalUsers();
    return () => { mounted = false; };
  }, []);

  // Edit mode: auto-select employee once internalUsers is loaded
  useEffect(() => {
    if (!editingId) return;
    if (selectedInternalUser) return;
    if (!internalUsers || internalUsers.length === 0) return;
    const row = rows.find(r => r.id === editingId);
    if (!row) return;

    const user = findInternalUserForRow(row, internalUsers);
    if (!user) return;

    setSelectedInternalUser(user);
    setEmployee(`${user.firstName} ${user.lastName}`.trim());
    setEmployeeId(user.employeeId || '');
  }, [editingId, internalUsers, rows, selectedInternalUser]);

  // Fetch personnel qualifications
  useEffect(() => {
    let mounted = true;
    const fetchQualifications = async () => {
      try {
        const res = await api.get('/audit/personnel-qualifications');
        if (mounted && res && res.data) {
          // Enhance data dengan groupId untuk upgrade logic
          const enhanced = res.data.map((q, idx) => ({
            ...q,
            id: q.id,
            qualification: q.qualification,
            qualificationGroup: q.qualificationGroup,
            level: typeof q.qualificationLevel === 'number' ? q.qualificationLevel : 0,
            // Create groupId dari qualificationGroup (lowercase, untuk grouping)
            groupId: String(q.qualificationGroup || '').toLowerCase().replace(/\s+/g, '-')
          }));
          setQualificationOptions(enhanced);
        }
      } catch (err) {
        console.error('Failed to fetch personnel qualifications:', err);
      }
    };
    fetchQualifications();
    return () => { mounted = false; };
  }, []);

  // Fetch competency requests
  const fetchCompetencies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/audit/competencies/requests');
      if (res && res.data) {
        setRows(res.data);
        console.log('📥 Raw API Response (all records):', res.data);
        console.log('📥 Approved records only:', res.data.filter(c => c.status === 'approved'));
        
        // Extract approved qualifications dari rows untuk upgrade
        const approved = res.data
          .filter(c => normalizeCompetencyStatus(c.status) === 'approved')
          .map(c => {
            // Find matching qualification option to get level and groupId
            const matchingQual = qualificationOptions.find(q => 
              q.qualification === c.qualificationName || String(q.id) === String(c.qualificationId)
            );
            const employeeName = c.employeeName || `${c.firstName || ''} ${c.lastName || ''}`.trim();
            console.log('📋 Processing approved competency:', {
              id: c.id,
              user_id: c.user_id,
              employeeId: c.employeeId,
              qualificationName: c.qualificationName,
              employeeName: employeeName,
              firstName: c.firstName,
              lastName: c.lastName,
              status: c.status,
              allFields: Object.keys(c)
            });
            return {
              id: c.id,
              user_id: c.user_id,
              employeeId: c.employeeId,
              employeeName: employeeName,
              firstName: c.firstName,
              lastName: c.lastName,
              qualificationId: c.qualificationId,
              qualificationName: c.qualificationName,
              standard: c.standard,
              sectors: c.sectors || '',
              level: matchingQual?.level || 0,
              groupId: matchingQual?.groupId || ''
            };
          });
        console.log('✅ All approved qualifications extracted:', approved);
        setApprovedQualifications(approved);
      }
    } catch (err) {
      console.error('Failed to fetch competencies:', err);
      alert('Failed to load competency requests');
    }
    setLoading(false);
  };

  // Helper: Get approved qualifications untuk selected employee saja (match by user_id only)
  const getEmployeeApprovedQualifications = (selectedUser) => {
    if (!selectedUser) return [];
    
    // 🔑 Use selectedUser.id as the user_id for matching (this is the primary key from User table)
    const selectedUserId = String(selectedUser.id || selectedUser.user_id || '').trim();
    
    console.log('🔍 Filtering approved qualifications by user_id:', {
      selectedUserId,
      fromField: selectedUser.id ? 'id' : 'user_id',
      totalApprovedRecords: approvedQualifications.length,
      selectedUser: {id: selectedUser.id, user_id: selectedUser.user_id, employeeId: selectedUser.employeeId}
    });
    
    const filtered = approvedQualifications.filter(c => {
      const approvedUserId = String(c.user_id || '').trim();
      const match = approvedUserId === selectedUserId;
      if (match) {
        console.log('✓ Found approved qualification for selected user:', {
          qualificationName: c.qualificationName,
          level: c.level,
          groupId: c.groupId,
          userId: c.user_id
        });
      }
      return match;
    });
    
    console.log('✅ Matched approved qualifications for user ' + selectedUserId + ':', filtered.length, filtered);
    return filtered;
  };

  const getAvailableLevelsForUpgrade = (qualificationGroupId) => {
    if (!qualificationGroupId) return [];
    // Upgrade = level angka lebih kecil (misal Level 4 → Level 3, 2, 1)
    return qualificationOptions
      .filter(q => q.groupId === qualificationGroupId && q.level < currentTypeLevel)
      .sort((a, b) => b.level - a.level)  // 3, 2, 1 (closest first)
      .map(q => ({
        id: q.id,
        text: `${q.qualification} (Level ${q.level})`
      }));
  };

  // Initial load of competencies
  useEffect(() => {
    fetchCompetencies();
  }, []);

  // Update approved qualifications whenever rows or qualificationOptions change
  // This ensures levels are correctly populated
  useEffect(() => {
    if (!rows || rows.length === 0) {
      console.log('No rows yet');
      return;
    }
    
    const approved = rows
      .filter(c => normalizeCompetencyStatus(c.status) === 'approved')
      .map(c => {
        // Find matching qualification option to get level and groupId
        const matchingQual = qualificationOptions.find(q => 
          q.qualification === c.qualificationName || String(q.id) === String(c.qualificationId)
        );
        const employeeName = c.employeeName || `${c.firstName || ''} ${c.lastName || ''}`.trim();
        return {
          id: c.id,
          user_id: c.user_id,
          employeeId: c.employeeId,
          employeeName: employeeName,
          firstName: c.firstName,
          lastName: c.lastName,
          qualificationId: c.qualificationId,
          qualificationName: c.qualificationName,
          standard: c.standard,
          sectors: c.sectors || '',
          level: matchingQual?.level || 0,
          groupId: matchingQual?.groupId || ''
        };
      });
    console.log('🔄 Re-syncing approved qualifications with level info:', approved);
    setApprovedQualifications(approved);
  }, [rows, qualificationOptions]);

  const openModal = (rowId = null) => {
    if (rowId) {
      // Edit mode
      const row = rows.find(r => r.id === rowId);
      if (row && isEditableCompetencyStatus(row.status)) {
        setEditingId(rowId);
        
        // Find user by employeeId / id / name (robust matching)
        const user = findInternalUserForRow(row, internalUsers);
        
        const matchedStandard = standardOptions.find(s => s.name === row.standard || String(s.id) === String(row.standard));
        
        // Parse sectors string to find matching sector IDs
        let sectorIds = [];
        if (row.sectors) {
          const sectorNames = row.sectors.split(',').map(s => s.trim());
          sectorIds = sectors
            .filter(sector => sectorNames.some(name => 
              sector.name.toLowerCase().includes(name.toLowerCase()) ||
              sector.code?.toLowerCase() === name.toLowerCase()
            ))
            .map(sector => sector.id);
        }
        
        setSelectedInternalUser(user || null);
        setEmployee(user ? `${user.firstName} ${user.lastName}`.trim() : getEmployeeDisplayNameById(row.employeeId));
        setEmployeeId(row.employeeId || '');
        setStandard(matchedStandard ? String(matchedStandard.id) : '');
        setQualificationId(row.qualificationId ? String(row.qualificationId) : '');
        setJustification(row.justification || '');
        setSelectedSectors([]);
        setRequestType(row.requestType || 'New Request');
        
        // For upgrade: set currentType dan requestedType
        if (row.requestType === 'Upgrade' && row.currentTypeId && user) {
          console.log('📝 Edit mode - Loading upgrade request for user:', user.id || user.user_id);
          
          const userApprovedQuals = getEmployeeApprovedQualifications(user);
          console.log('🔍 User approved qualifications:', userApprovedQuals);
          
          // currentTypeId berisi qualificationId (relasi FK)
          const currQual = userApprovedQuals.find(q => 
            String(q.qualificationId) === String(row.currentTypeId)
          );
          
          console.log('📋 Current qualification found:', currQual);
          
          if (currQual) {
            setCurrentType(String(currQual.qualificationId));
            setCurrentTypeLevel(currQual.level || 0);
            
            // Find matching qualification in options to get groupId
            const matchingQualInOptions = qualificationOptions.find(q => 
              q.qualification === currQual.qualificationName || String(q.id) === String(currQual.qualificationId)
            );
            const groupId = matchingQualInOptions?.groupId || currQual.groupId;
            const currentLevel = currQual.level || 0;
            
            // Calculate available target levels (upgrade = smaller numeric level)
            const availableLevels = qualificationOptions
              .filter(q => q.groupId === groupId && q.level < currentLevel)
              .sort((a, b) => b.level - a.level)  // 3, 2, 1 (closest first)
              .map(q => ({
                id: String(q.id),
                text: `${q.qualification} (Level ${q.level})`
              }));
            
            console.log('🎯 Available target levels in edit mode:', {groupId, currentLevel, availableLevels});
            setAvailableLevelsForUpgrade(availableLevels);
            
            // Find and set requestedType (disimpan sebagai FK ID)
            const reqQual = qualificationOptions.find(q => String(q.id) === String(row.requestedTypeId));
            const reqQualId = reqQual ? String(reqQual.id) : '';
            setRequestedType(reqQualId);
            
            // Untuk Upgrade: qualificationId harus = target qualification
            if (reqQualId) {
              setQualificationId(reqQualId);
              console.log('📋 Edit Upgrade: qualification set to target:', reqQual?.qualification);
            }
          } else {
            console.warn('⚠️ Current qualification not found in approved list for selected user');
            setCurrentType('');
            setRequestedType('');
            setCurrentTypeLevel(0);
            setAvailableLevelsForUpgrade([]);
          }
        } else {
          setCurrentType('');
          setRequestedType('');
          setCurrentTypeLevel(0);
          setAvailableLevelsForUpgrade([]);
        }
        
        setStandards([{
          id: matchedStandard ? matchedStandard.id : row.standard,
          name: matchedStandard ? matchedStandard.name : row.standard,
          standard: matchedStandard ? String(matchedStandard.id) : row.standard,
          sectorIds: sectorIds,
          sectorCodes: sectorIds,
          documentPath: row.documentPath || '',
          documentName: row.documentName || row.documentPath || '',
          documentFile: null,
          uid: Date.now()
        }]);
      } else {
        alert('Can only edit pending or need revision requests');
        return;
      }
    } else {
      // New request mode - reset form
      setEditingId(null);
      setEmployee('');
      setEmployeeId('');
      setStandard('');
      setQualificationId('');
      setJustification('');
      setSelectedSectors([]);
      setRequestType('New Request');
      setCurrentType('');
      setRequestedType('');
      setCurrentTypeLevel(0);
      setAvailableLevelsForUpgrade([]);
      setStandards([]);
      setStandardSearchQuery('');
    }
    
    // Increment modal key first
    modalKeyRef.current += 1;
    
    // Delay modal opening to ensure state updates are applied
    setTimeout(() => {
      const el = document.getElementById('modal-competency');
      if (!el) return;
      const modal = new window.bootstrap.Modal(el, { backdrop: 'static', keyboard: true });
      modal.show();
    }, 50);
  };

  const openReviewModal = (row, mode = 'edit') => {
    if (!row) {
      alert('No request selected');
      return;
    }
    const isEdit = mode === 'edit';
    if (isEdit && !isEditableCompetencyStatus(row.status)) {
      alert('Can only review pending or need revision requests');
      return;
    }
    setReviewMode(mode);
    setReviewData(row);
    setReviewNotes(isEdit ? '' : (row.reviewNotes || ''));
    setReviewDecision(isEdit ? 'approve' : mapStatusToDecision(row.status));
    const el = document.getElementById('modal-review-competency');
    if (!el) return;
    const modal = new window.bootstrap.Modal(el, { backdrop: 'static', keyboard: true });
    modal.show();
  };

  const filteredRows = rows.filter((row) => {
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      const haystack = `${getEmployeeDisplayNameById(row.employeeId) || ''} ${row.standard || ''} ${row.sectors || ''}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (standardFilter && String(row.standard) !== String(standardFilter)) {
      return false;
    }
    if (statusFilter && normalizeCompetencyStatus(row.status) !== statusFilter) {
      return false;
    }
    return true;
  });

  // Group rows by employee, then by standard, so each person appears once and each standard
  // appears once with all its qualifications combined into a single cell
  const groupedByEmployee = (() => {
    const map = new Map();
    filteredRows.forEach(row => {
      const key = String(row.employeeId || getEmployeeDisplayNameById(row.employeeId) || '');
      if (!map.has(key)) {
        map.set(key, { key, name: getEmployeeDisplayNameById(row.employeeId), standardMap: new Map() });
      }
      const group = map.get(key);
      const standardKey = String(row.standard || '');
      if (!group.standardMap.has(standardKey)) {
        group.standardMap.set(standardKey, { standard: row.standard, rows: [] });
      }
      group.standardMap.get(standardKey).rows.push(row);
    });
    return Array.from(map.values()).map(group => ({
      ...group,
      standardGroups: Array.from(group.standardMap.values())
    }));
  })();

  const totalPages = Math.max(1, Math.ceil(groupedByEmployee.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedGroups = groupedByEmployee.slice(startIndex, startIndex + pageSize);

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, standardFilter, statusFilter, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const hideReviewModal = () => {
    const el = document.getElementById('modal-review-competency');
    if (!el) return;
    const m = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
    m.hide();
  };

  const handleReviewDecision = async () => {
    if (reviewMode !== 'edit') {
      return;
    }
    if (!reviewData || !reviewNotes.trim()) {
      alert('Please provide review notes');
      return;
    }

    try {
      const payload = {
        competencyId: reviewData.id,
        reviewDecision: reviewDecision,
        reviewNotes: reviewNotes
      };
      await api.post(`/audit/competencies/request/${reviewData.id}/review`, payload);
      alert('Review submitted successfully');
      hideReviewModal();
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to submit review:', err);
      alert('Failed to submit review');
    }
  };

  const handleSubmitForm = async (e) => {
    if (!canCompetency('create')) { alert('You do not have permission to create.'); return; }
    e.preventDefault();
    
    // Validate that employeeId is actually provided (not empty)
    const finalEmployeeId = getSelectedEmployeeRequestId();

    const selectedQualification = qualificationOptions.find((item) => String(item.id) === String(qualificationId));
    const qualificationName = String(selectedQualification?.qualification || '').toLowerCase();
    const standardIsOptional = ['scheduler', 'certificate issuance', 'certificate reviewer', 'certification authority']
      .some((role) => qualificationName.includes(role));

    if (!finalEmployeeId || !qualificationId) {
      alert('Please select an employee and fill all required fields');
      return;
    }

    // Regular Auditor/Lead Auditor competency remains scoped to a standard (must check at least
    // one standard checkbox). Operational Scheduler/Certificate roles are role-only competency
    // records and don't require any standard to be checked.
    if (!standardIsOptional && standards.length === 0) {
      alert('Please select at least one standard');
      return;
    }

    // Validasi untuk upgrade
    if (requestType === 'Upgrade') {
      if (!currentType || !requestedType) {
        alert('Please select both Current Type and Requested Type for upgrade request');
        return;
      }
      const currentQual = qualificationOptions.find(q => String(q.id) === String(currentType));
      const requestedQual = qualificationOptions.find(q => String(q.id) === String(requestedType));
      if (currentQual && requestedQual && currentQual.groupId !== requestedQual.groupId) {
        alert('Current Type dan Requested Type harus dalam qualification group yang sama');
        return;
      }
      if (currentQual && requestedQual && requestedQual.level >= currentQual.level) {
        alert('Target level harus lebih kecil dari Current Type level (upgrade = angka level lebih kecil)');
        return;
      }
    }

    // Standardless roles submit a single role-only request when no standard is checked
    const standardsToSubmit = standards.length > 0 ? standards : [createEmptyStandardItem()];

    try {
      let successCount = 0;
      let failedCount = 0;

      const uploadDocumentIfNeeded = async (file) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/audit/competencies/document', formData);
        return res?.data || null;
      };

      const buildPayload = async (item) => {
        const payload = {
          employeeId: getSelectedEmployeeRequestId(),
          standard: item.id ? String(item.id) : (item.standard || ''),
          qualificationId: qualificationId,
          justification: justification || '',
          sectorIds: Array.isArray(item.sectorIds)
            ? item.sectorIds
            : (Array.isArray(item.sectorCodes) ? item.sectorCodes : []),
          requestType: requestType,
          // Untuk Upgrade: simpan qualification ID ke current_type_id & requested_type_id (relasi FK)
          currentTypeId: requestType === 'Upgrade' ? Number(currentType) : undefined,
          requestedTypeId: requestType === 'Upgrade' ? Number(requestedType) : undefined
        };
        
        // Upload file (keep file utuh), store only URL/path in backend
        if (item.documentFile) {
          const uploadResult = await uploadDocumentIfNeeded(item.documentFile);
          const pathOrUrl = uploadResult?.url || uploadResult?.path;
          if (!pathOrUrl) {
            throw new Error('Upload document failed: no URL returned');
          }
          payload.documentPath = String(pathOrUrl);
          payload.documentName = uploadResult?.name || item.documentFile.name;
        } else if (item.documentPath) {
          // Keep existing document if no new file
          payload.documentPath = item.documentPath;
          payload.documentName = item.documentName || item.documentPath;
        }
        
        return payload;
      };

      if (editingId) {
        // Update mode - only update the first standard
        const primaryStandard = standardsToSubmit[0] || {};
        const payload = await buildPayload(primaryStandard);
        await api.put(`/audit/competencies/request/${editingId}`, payload);
        successCount = 1;
      } else {
        // Create mode - submit each standard as separate request
        for (const standard of standardsToSubmit) {
          const payload = await buildPayload(standard);
          try {
            await api.post('/audit/competencies/request', payload);
            successCount++;
          } catch (err) {
            console.error(`Failed to submit standard ${standard.name || standard.standard}:`, err);
            failedCount++;
          }
        }
      }

      // Generate success message
      let message = '';
      if (editingId) {
        message = 'Request updated successfully';
      } else {
        if (standardsToSubmit.length === 1) {
          message = 'Request submitted successfully';
        } else {
          message = `${successCount} of ${standardsToSubmit.length} requests submitted successfully`;
          if (failedCount > 0) {
            message += ` (${failedCount} failed)`;
          }
        }
      }
      alert(message);

      // Close modal and refresh
      const el = document.getElementById('modal-competency');
      if (el) {
        const m = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
        m.hide();
      }
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to submit request:', err);
      const backendMessage = err.response?.data?.message;
      const backendRaw = typeof err.response?.data === 'string' ? err.response.data : null;
      alert('Failed to submit request: ' + (backendMessage || backendRaw || err.message));
    }
  };

  const openDeactivateModal = (row) => {
    if (!canCompetency('edit')) { alert('You do not have permission to change this status.'); return; }
    setDeactivateTarget(row);
    setDeactivateNotes('');
    setDeactivateError('');
  };

  const closeDeactivateModal = () => {
    setDeactivateTarget(null);
    setDeactivateNotes('');
    setDeactivateError('');
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    if (!deactivateNotes.trim()) {
      setDeactivateError('Notes are required to deactivate a competency.');
      return;
    }
    setDeactivateLoading(true);
    try {
      await api.patch(`/audit/competencies/request/${deactivateTarget.id}/status`, {
        active: false,
        notes: deactivateNotes.trim()
      });
      closeDeactivateModal();
      alert('Competency deactivated successfully');
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to deactivate competency:', err);
      setDeactivateError(err?.response?.data?.message || 'Failed to deactivate competency');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleActivate = async (row) => {
    if (!canCompetency('edit')) { alert('You do not have permission to change this status.'); return; }
    if (!window.confirm('Reactivate this competency?')) return;
    try {
      await api.patch(`/audit/competencies/request/${row.id}/status`, { active: true });
      alert('Competency activated successfully');
      fetchCompetencies();
    } catch (err) {
      console.error('Failed to activate competency:', err);
      alert(err?.response?.data?.message || 'Failed to activate competency');
    }
  };

  // Reset form when modal is hidden
  useEffect(() => {
    const modalEl = document.getElementById('modal-competency');
    if (!modalEl) return;

    const handleHide = () => {
      setEditingId(null);
      setEmployee('');
      setEmployeeId('');
      setStandard('');
      setQualificationId('');
      setSelectedSectors([]);
      setJustification('');
      setStandards([]);
      setStandardSearchQuery('');
      setSelectedInternalUser(null);
      setRequestType('New Request');
      setCurrentType('');
      setRequestedType('');
      setCurrentTypeLevel(0);
      setAvailableLevelsForUpgrade([]);
    };

    modalEl.addEventListener('hidden.bs.modal', handleHide);
    return () => modalEl.removeEventListener('hidden.bs.modal', handleHide);
  }, []);

  const handleTomSelectChange = (name, value) => {
    if (name === 'sectorCodes') {
      setSelectedSectors(Array.isArray(value) ? value : (value ? [value] : []));
    }
  };
  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Competency Management
              </h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={() => openModal()}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Submit Competency
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Search and Filters */}
                <div className="row align-items-end g-2 mb-4">
                  <div className="col-md-4">
                    <label className="form-label">Search</label>
                    <div className="input-icon">
                      <span className="input-icon-addon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" /></svg>
                      </span>
                      <input
                        className="form-control"
                        placeholder="Search by name, standard..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Standard</label>
                    <select
                      className="form-select"
                      value={standardFilter}
                      onChange={(e) => setStandardFilter(e.target.value)}
                    >
                      <option value="">All Standards</option>
                      {standardOptions.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending Approval</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="need_revision">Need Revision</option>
                    </select>
                  </div>
                  <div className="col-auto">
                    <button className="btn btn-outline-secondary d-flex align-items-center gap-1" type="button" onClick={() => setPage(1)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                      Filter
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-hover">
                    <thead>
                      <tr>
                        <th style={{width: '40px'}}><input type="checkbox" className="form-check-input" /></th>
                        <th>Name</th>
                        <th>Standard</th>
                        <th>Qualification</th>
                        <th>Sector/Scheme</th>
                        <th>Request Date</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="text-muted small mt-2">Loading competency requests...</div>
                          </td>
                        </tr>
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted mb-2">
                              <rect x="3" y="4" width="18" height="16" rx="2" />
                              <path d="M8 10h8M8 14h5" />
                            </svg>
                            <div className="text-muted">No competency requests found</div>
                          </td>
                        </tr>
                      ) : (
                        paginatedGroups.map((group, groupIdx) => {
                          const isExpanded = expandedGroups.has(group.key);
                          const hasMore = group.standardGroups.length > STANDARD_COLLAPSE_LIMIT;
                          const visibleStandardGroups = isExpanded
                            ? group.standardGroups
                            : group.standardGroups.slice(0, STANDARD_COLLAPSE_LIMIT);
                          const rowSpanCount = visibleStandardGroups.length + (hasMore ? 1 : 0);

                          const initials = group.name
                            ? group.name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase()
                            : '?';

                          return visibleStandardGroups.map((standardGroup, sgIdx) => {
                            const isGroupFirstRow = sgIdx === 0;
                            const uniqueSectors = Array.from(new Set(
                              standardGroup.rows.map(r => r.sectors).filter(Boolean)
                            ));
                            return (
                            <tr
                              key={`${group.key}-${standardGroup.standard}-${sgIdx}`}
                              className={isGroupFirstRow ? 'border-top border-top-2' : ''}
                            >
                              {isGroupFirstRow && (
                                <td rowSpan={rowSpanCount} className="align-top">
                                  <input type="checkbox" className="form-check-input" />
                                </td>
                              )}
                              {isGroupFirstRow && (
                                <td rowSpan={rowSpanCount} className="align-top">
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      className="avatar avatar-sm rounded-circle bg-primary-lt text-primary fw-bold"
                                      style={{ flexShrink: 0 }}
                                    >
                                      {initials}
                                    </span>
                                    <strong>{group.name}</strong>
                                  </div>
                                </td>
                              )}
                              <td className="align-top"><span className="fw-medium">{standardGroup.standard}</span></td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  {standardGroup.rows.map(row => {
                                    const qualification = qualificationOptions.find(q => String(q.id) === String(row.qualificationId));
                                    const isUpgrade = row.requestType === 'Upgrade';
                                    const currentQual = isUpgrade ? qualificationOptions.find(q => String(q.id) === String(row.currentTypeId)) : null;
                                    const requestedQual = isUpgrade ? qualificationOptions.find(q => String(q.id) === String(row.requestedTypeId)) : null;
                                    const currentQualName = currentQual?.qualification || row.currentTypeName || '';
                                    const requestedQualName = requestedQual?.qualification || row.requestedTypeName || '';
                                    return (
                                      <div key={row.id} className="d-flex align-items-center gap-1">
                                        {isUpgrade && currentQualName && requestedQualName ? (
                                          <>
                                            <span className="badge bg-info-lt rounded-pill">{currentQualName}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            <span className="badge bg-success-lt rounded-pill">{requestedQualName}</span>
                                          </>
                                        ) : (
                                          <span className="badge bg-info-lt rounded-pill">{qualification ? qualification.qualification : 'N/A'}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                              <td className="align-top"><span className="text-muted small">{uniqueSectors.join(', ') || '—'}</span></td>
                              <td className="align-top">
                                <div className="d-flex flex-column gap-1">
                                  {standardGroup.rows.map(row => (
                                    <span key={row.id} className="text-muted small">{formatDate(row.requestDate)}</span>
                                  ))}
                                </div>
                              </td>
                              <td className="align-top">
                                <div className="d-flex flex-column gap-1">
                                  {standardGroup.rows.map(row => (
                                    <span key={row.id}>{statusBadge(row.status)}</span>
                                  ))}
                                </div>
                              </td>
                              <td className="align-top">
                                <div className="d-flex flex-column gap-1">
                                  {standardGroup.rows.map(row => (
                                    <div key={row.id} className="btn-list gap-1 flex-nowrap">
                                      {isEditableCompetencyStatus(row.status) && (
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => openModal(row.id)}>Edit</button>
                                      )}
                                      {isEditableCompetencyStatus(row.status) && (
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openReviewModal(row, 'edit')}>Review</button>
                                      )}
                                      {!isEditableCompetencyStatus(row.status) && (
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openReviewModal(row, 'view')}>View</button>
                                      )}
                                      {isRowActive(row) ? (
                                        <button className="btn btn-sm btn-outline-warning" onClick={() => openDeactivateModal(row)}>Deactivate</button>
                                      ) : (
                                        <button className="btn btn-sm btn-outline-success" onClick={() => handleActivate(row)}>Activate</button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                            );
                          }).concat(
                            hasMore ? [(
                              <tr key={`${group.key}-toggle`}>
                                <td colSpan="6" className="text-center py-1">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-ghost-secondary d-inline-flex align-items-center gap-1"
                                    onClick={() => toggleGroupExpanded(group.key)}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
                                    >
                                      <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                    {isExpanded
                                      ? 'Show less'
                                      : `Show ${group.standardGroups.length - STANDARD_COLLAPSE_LIMIT} more standard${group.standardGroups.length - STANDARD_COLLAPSE_LIMIT === 1 ? '' : 's'}`}
                                  </button>
                                </td>
                              </tr>
                            )] : []
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between mt-4">
                  <div>
                    <label className="form-label small mb-1">People per page</label>
                    <select
                      className="form-select form-select-sm"
                      style={{width: '100px'}}
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm m-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          type="button"
                          aria-label="Previous"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                      </li>
                      {getPageNumbers().map((pageNumber) => (
                        <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                          <button className="page-link" type="button" onClick={() => setPage(pageNumber)}>
                            {pageNumber}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          type="button"
                          aria-label="Next"
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
                    {/* Modal markup (Bootstrap) similar to Companies modal */}
      <div className="modal modal-blur fade" id="modal-competency" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <form onSubmit={handleSubmitForm} noValidate>
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Competency Request' : 'Submit Competency Request'}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  <strong>Form Competency Request</strong><br />
                  Ajukan permintaan kompetensi baru atau upgrade kompetensi yang sudah Anda miliki
                </p>

                {requestType === 'Upgrade' && (
                  <div className="alert alert-info mb-3" role="alert">
                    <strong><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1" style={{display: 'inline'}}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    Upgrade Kompetensi</strong><br />
                    <small>Pilih kompetensi saat ini yang sudah Anda miliki, kemudian pilih target level yang lebih tinggi (angka level lebih kecil). Contoh: Auditor Level 4 → Level 3, 2, atau 1.</small>
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Request Type</label>
                    <div className="btn-group w-100" role="group">
                      <input type="radio" className="btn-check" name="requestType" id="typeNew" value="New Request" checked={requestType === 'New Request'} onChange={(e) => setRequestType(e.target.value)} />
                      <label className="btn btn-outline-primary" htmlFor="typeNew">New Request</label>
                      
                      <input 
                        type="radio" 
                        className="btn-check" 
                        name="requestType" 
                        id="typeUpgrade" 
                        value="Upgrade" 
                        checked={requestType === 'Upgrade'} 
                        onChange={(e) => setRequestType(e.target.value)}
                        disabled={!selectedInternalUser || getEmployeeApprovedQualifications(selectedInternalUser).length === 0}
                      />
                      <label 
                        className="btn btn-outline-primary" 
                        htmlFor="typeUpgrade"
                        style={{ opacity: (!selectedInternalUser || getEmployeeApprovedQualifications(selectedInternalUser).length === 0) ? 0.5 : 1, cursor: (!selectedInternalUser || getEmployeeApprovedQualifications(selectedInternalUser).length === 0) ? 'not-allowed' : 'pointer' }}
                        title={!selectedInternalUser ? 'Pilih employee terlebih dahulu' : getEmployeeApprovedQualifications(selectedInternalUser).length === 0 ? 'Employee belum memiliki approved qualifications' : ''}
                      >
                        Upgrade
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <TomSelect
                      key={`employee-${modalKeyRef.current}`}
                      name="employee"
                      value={selectedInternalUser ? String(selectedInternalUser.id) : ''}
                      onChange={(_, value) => {
                        const user = internalUsers.find(u => String(u.id) === String(value));
                        console.log('👤 Employee selected:', {
                          selectedValue: value,
                          user: user ? { 
                            id: user.id, 
                            employeeId: user.employeeId, 
                            firstName: user.firstName, 
                            lastName: user.lastName,
                            fullName: `${user.firstName} ${user.lastName}`.trim()
                          } : null
                        });
                        setSelectedInternalUser(user);
                        setEmployee(user ? `${user.firstName} ${user.lastName}` : '');
                        setEmployeeId(user ? user.employeeId : '');
                        setCurrentType('');
                        setRequestedType('');
                        setCurrentTypeLevel(0);
                        setAvailableLevelsForUpgrade([]);
                        setQualificationId('');
                        setStandards([]);
      setStandardSearchQuery('');

                        if (user) {
                          const approvals = getEmployeeApprovedQualifications(user);
                          console.log('🔎 Approvals found immediately after selection:', approvals.length);
                          if (approvals.length === 0) {
                            setRequestType('New Request');
                          }
                        } else {
                          setRequestType('New Request');
                        }
                      }}
                      options={internalUsers.map(user => ({ value: String(user.id), text: `${user.firstName} ${user.lastName}` }))}
                      placeholder="Employee name"
                      label="Employee name"
                      valueField="value"
                      labelField="text"
                      searchField="text"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <TomSelect
                      key={`qualification-${modalKeyRef.current}-${qualificationId}`}
                      name="qualification"
                      value={qualificationId}
                      onChange={(_, value) => setQualificationId(value)}
                      disabled={requestType === 'Upgrade'}
                      options={qualificationOptions.map(q => ({ id: String(q.id), name: q.qualification }))}
                      placeholder="Select qualification"
                      label="Qualification"
                      valueField="id"
                      labelField="name"
                      searchField="name"
                      required
                    />
                  </div>

                  {requestType === 'Upgrade' && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Current Type <span className="text-danger">*</span></label>
                        <div className="small text-muted mb-2">Pilih dari kualifikasi Anda yang sudah approved</div>
                        <TomSelect
                          key={`currentType-${modalKeyRef.current}`}
                          name="currentType"
                          value={currentType}
                          onChange={(_, value) => {
                            console.log('🔄 Current Type selection changed to:', value);
                            setCurrentType(value);
                            
                            // Find from approved qualifications (not qualificationOptions)
                            const approvedQuals = getEmployeeApprovedQualifications(selectedInternalUser);
                            const selectedApprovedQual = approvedQuals.find(q => String(q.qualificationId) === String(value));
                            
                            console.log('📝 Current Type selected:', {
                              value,
                              selectedApprovedQual,
                              allApprovedQuals: approvedQuals
                            });
                            
                            if (selectedApprovedQual) {
                              setCurrentTypeLevel(selectedApprovedQual.level || 0);
                              
                              // 🔄 Auto-fill Standard ke standards array (row pertama)
                              let matchedStdId = '';
                              if (selectedApprovedQual.standard) {
                                const matchedStd = standardOptions.find(s => 
                                  s.name === selectedApprovedQual.standard || String(s.id) === String(selectedApprovedQual.standard)
                                );
                                if (matchedStd) {
                                  matchedStdId = String(matchedStd.id);
                                  console.log('📋 Auto-filled Standard:', matchedStd.name);
                                }
                              }
                              
                              // 🔄 Auto-fill Sector Codes dari approved qualification
                              let matchedSectorIds = [];
                              if (selectedApprovedQual.sectors) {
                                const sectorNames = selectedApprovedQual.sectors.split(',').map(s => s.trim());
                                matchedSectorIds = sectors
                                  .filter(sector => sectorNames.some(name => 
                                    sector.name?.toLowerCase().includes(name.toLowerCase()) ||
                                    sector.code?.toLowerCase() === name.toLowerCase()
                                  ))
                                  .map(sector => sector.id);
                                if (matchedSectorIds.length > 0) {
                                  console.log('📋 Auto-filled Sectors:', matchedSectorIds);
                                }
                              }
                              
                              // 🔄 Update standards array (row pertama) dengan standard + sectors
                              setStandards(prev => {
                                const updated = [...prev];
                                if (updated.length === 0) {
                                  updated.push(createEmptyStandardItem());
                                }
                                updated[0] = {
                                  ...updated[0],
                                  standard: matchedStdId,
                                  id: matchedStdId,
                                  sectorCodes: matchedSectorIds.length > 0 ? matchedSectorIds : updated[0].sectorCodes,
                                  sectorIds: matchedSectorIds.length > 0 ? matchedSectorIds : updated[0].sectorIds,
                                };
                                return updated;
                              });
                              
                              // 🔄 Untuk Upgrade: qualification akan di-set saat memilih Target (Requested Type)
                              // Bukan dari Current Type, karena qualification harus = target qualification
                              setQualificationId('');
                              
                              // Update available levels based on groupId and level from approved qualification
                              const matchingQualInOptions = qualificationOptions.find(q => 
                                q.qualification === selectedApprovedQual.qualificationName || String(q.id) === String(selectedApprovedQual.qualificationId)
                              );
                              const groupId = matchingQualInOptions?.groupId || selectedApprovedQual.groupId;
                              const currentLevel = selectedApprovedQual.level || 0;
                              
                              const available = qualificationOptions
                                .filter(q => q.groupId === groupId && q.level < currentLevel)
                                .sort((a, b) => b.level - a.level)
                                .map(q => ({
                                  id: String(q.id),
                                  text: `${q.qualification} (Level ${q.level})`
                                }));
                              
                              console.log('🎯 Available target levels:', {groupId, currentLevel, count: available.length, options: available});
                              setAvailableLevelsForUpgrade(available);
                              
                              if (available.length === 0) {
                                console.warn('⚠️  No higher levels available in this group for upgrade');
                              }
                            } else {
                              console.warn('⚠️  Selected qualification not found in approved list');
                              setCurrentTypeLevel(0);
                              setAvailableLevelsForUpgrade([]);
                            }
                            
                            setRequestedType(''); // Reset requested type when current changes
                          }}
                          options={getEmployeeApprovedQualifications(selectedInternalUser).map(q => ({ 
                            id: String(q.qualificationId), 
                            text: `${q.qualificationName} (Level ${q.level || 0})` 
                          }))}
                          placeholder={getEmployeeApprovedQualifications(selectedInternalUser).length === 0 ? "Employee belum memiliki approved qualifications" : "Select your current qualification"}
                          label="Current Qualification"
                          valueField="id"
                          labelField="text"
                          searchField="text"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Requested Type <span className="text-danger">*</span></label>
                        <div className="small text-muted mb-2">Pilih target level yang lebih tinggi (angka level lebih kecil dari kualifikasi saat ini)</div>
                        <TomSelect
                          key={`requestedType-${modalKeyRef.current}-${availableLevelsForUpgrade.length}-${currentType}`}
                          name="requestedType"
                          value={requestedType}
                          onChange={(_, value) => {
                            console.log('🔄 Requested Type selection changed to:', value);
                            const selected = availableLevelsForUpgrade.find(q => String(q.id) === String(value));
                            console.log('📝 Requested Type selected:', {value, selected});
                            setRequestedType(value);
                            // 🔄 Untuk Upgrade: qualification = target qualification
                            setQualificationId(value);
                            console.log('📋 Qualification set to target:', value);
                          }}
                          options={availableLevelsForUpgrade.map(q => ({ 
                            id: String(q.id), 
                            text: q.text 
                          }))}
                          placeholder={
                            currentType ? 
                              (availableLevelsForUpgrade.length === 0 ? 
                                "Tidak ada level lebih tinggi tersedia untuk upgrade" : 
                                "Select target qualification level") : 
                              "Pilih Current Type terlebih dahulu"
                          }
                          label="Target Qualification"
                          valueField="id"
                          labelField="text"
                          searchField="text"
                          disabled={!currentType || availableLevelsForUpgrade.length === 0}
                          required
                        />
                        {currentType && availableLevelsForUpgrade.length > 0 && (
                          <small className="text-success">✓ {availableLevelsForUpgrade.length} level tersedia untuk dipilih</small>
                        )}
                      </div>
                    </>
                  )}

                  <div className="col-12">
                    <label className="form-label">Competency</label>
                    <div className="text-muted mb-2" style={{ fontSize: '0.95em' }}>
                      Select one or more standards according to the competency and the completeness of the documentation.
                    </div>
                    {['scheduler', 'certificate issuance', 'certificate reviewer', 'certification authority']
                      .some((role) => String(qualificationOptions.find((q) => String(q.id) === String(qualificationId))?.qualification || '').toLowerCase().includes(role)) && (
                      <div className="alert alert-info py-2 px-3 small mb-2" role="alert">
                        Standard selection is optional for this qualification — this is a role-only competency.
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="small text-muted">
                        {standards.length} of {standardOptions.length} standard{standardOptions.length === 1 ? '' : 's'} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const allChecked = standardOptions.length > 0 && standards.length === standardOptions.length;
                          if (allChecked) {
                            setStandards([]);
                          } else {
                            setStandards(standardOptions.map(s => {
                              const existing = standards.find(item => String(item.standard || item.id) === String(s.id));
                              return existing || { ...createEmptyStandardItem(), standard: String(s.id), id: String(s.id) };
                            }));
                          }
                        }}
                      >
                        {standardOptions.length > 0 && standards.length === standardOptions.length ? 'Uncheck All' : 'Check All'}
                      </button>
                    </div>

                    <div className="input-icon mb-2">
                      <span className="input-icon-addon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" /></svg>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search standard..."
                        value={standardSearchQuery}
                        onChange={(e) => setStandardSearchQuery(e.target.value)}
                      />
                    </div>

                    <div
                      className="border rounded p-2 mb-3"
                      style={{ maxHeight: 180, overflowY: 'auto' }}
                    >
                      <div className="row g-1">
                        {standardOptions
                          .filter(s => s.name?.toLowerCase().includes(standardSearchQuery.trim().toLowerCase()))
                          .map(s => {
                          const isChecked = standards.some(item => String(item.standard || item.id) === String(s.id));
                          return (
                            <div className="col-md-4 col-sm-6" key={s.id}>
                              <label className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    setStandards(prev => {
                                      const exists = prev.some(item => String(item.standard || item.id) === String(s.id));
                                      if (exists) {
                                        return prev.filter(item => String(item.standard || item.id) !== String(s.id));
                                      }
                                      return [...prev, { ...createEmptyStandardItem(), standard: String(s.id), id: String(s.id) }];
                                    });
                                  }}
                                />
                                <span className="form-check-label">{s.name}</span>
                              </label>
                            </div>
                          );
                        })}
                        {standardOptions.length > 0 && standardOptions.filter(s => s.name?.toLowerCase().includes(standardSearchQuery.trim().toLowerCase())).length === 0 && (
                          <div className="col-12 text-muted small text-center py-2">No standards match "{standardSearchQuery}"</div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {standards.map((item, idx) => {
                        const matchedStandard = standardOptions.find(s => String(s.id) === String(item.standard || item.id));
                        return (
                        <div key={item.uid || item.id || idx} className="row g-2 align-items-end p-2" style={{ border: '1px solid #eee', borderRadius: 8 }}>
                          <div className="col-12 d-flex align-items-center justify-content-between">
                            <strong>{matchedStandard ? matchedStandard.name : 'Standard'}</strong>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setStandards(prev => prev.filter((_, i) => i !== idx))}
                            >
                              Remove
                            </button>
                          </div>
                          <div className="col-12">
                            <TomSelect
                              key={`sectorCodes-${modalKeyRef.current}-${idx}-${(item.sectorCodes || item.sectorIds || []).join(',')}`}
                              name="sectorCodes"
                              value={item.sectorCodes || item.sectorIds || []}
                              onChange={(_, value) => {
                                setStandards(prev => prev.map((s, i) => i === idx ? { ...s, sectorCodes: value, sectorIds: value } : s));
                              }}
                              options={sectors}
                              placeholder="Select sector code"
                              label="Sector Codes"
                              valueField="id"
                              labelField="name"
                              searchField="name"
                              multiple={true}
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label mb-1">Supporting Document (optional)</label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                setStandards(prev => prev.map((s, i) => i === idx ? { ...s, documentFile: file } : s));
                              }}
                            />
                            {item.documentPath && !item.documentFile && (
                              <div className="small mt-2">
                                <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                  </svg>
                                  <div className="flex-fill">
                                    <div className="small text-muted">Current file:</div>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-decoration-none text-start"
                                      onClick={() => {
                                        setPreviewFile(item.documentPath);
                                        setPreviewFileName(item.documentName || 'document');
                                        setShowPreviewModal(true);
                                      }}
                                    >
                                      <strong>{item.documentName || item.documentPath}</strong>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {item.documentFile && (
                              <div className="small text-success mt-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                New file selected: <strong>{item.documentFile.name}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Justification</label>
                    <textarea className="form-control" rows="4" value={justification} onChange={e => setJustification(e.target.value)} placeholder="Write justification..." />
                  </div>
                </div>

              </div>
              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary" data-bs-dismiss="modal">Cancel</a>
                <button type="submit" className="btn btn-success">{editingId ? 'Update Request' : 'Send Request'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Review Competency Modal */}
      <div className="modal modal-blur fade" id="modal-review-competency" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header border-bottom">
              <div>
                <h5 className="modal-title mb-1">
                  {reviewMode === 'view' ? 'Review Details' : 'Review Competency Request'}
                </h5>
                <div className="small text-muted">
                  {reviewMode === 'view'
                    ? 'View historical review decision and notes'
                    : 'Review and approve/reject the competency upgrade request'}
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {reviewData ? (
                <>
                  {(() => {
                    const qualification = qualificationOptions.find(q => q.id === reviewData.qualificationId);
                    return (
                      <>
                        {/* Top Section - Auditor Info */}
                        <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="small text-muted mb-1">Auditor</div>
                      <div className="mb-3"><strong>{getEmployeeDisplayNameById(reviewData.employeeId)}</strong></div>
                      <div className="small text-muted mb-1">Standard</div>
                      <div className="mb-3"><strong>{reviewData.standard}</strong></div>
                      <div className="small text-muted mb-1">Qualification</div>
                      <div className="mb-3"><span className="badge bg-info text-white">{qualification ? qualification.qualification : 'N/A'}</span></div>
                      <div className="small text-muted mb-1">Sector Codes / Scheme</div>
                      <div><strong>{reviewData.sectors}</strong></div>
                    </div>
                    <div className="col-md-6">
                      {/* <div className="small text-muted mb-1">Employee ID</div>
                      <div className="mb-3"><strong>{reviewData.employeeId}</strong></div> */}
                      <div className="small text-muted mb-1">Status</div>
                      <div className="mb-3">{statusBadge(reviewData.status)}</div>
                      {reviewData.reviewDate && (
                        <>
                          <div className="small text-muted mb-1">Review Date</div>
                          <div className="mb-3"><strong>{formatDate(reviewData.reviewDate)}</strong></div>
                        </>
                      )}
                      {reviewData.reviewedBy && (
                        <>
                          <div className="small text-muted mb-1">Reviewed By</div>
                          <div className="mb-3"><strong>{reviewData.reviewedBy}</strong></div>
                        </>
                      )}
                    </div>
                  </div>

                  <hr className="my-3" />

                  {/* Justification */}
                  <div className="mb-3">
                    <label className="form-label small mb-2">Justification</label>
                    <div className="p-3 border rounded small bg-light" style={{whiteSpace: 'pre-wrap', minHeight: '80px'}}>{reviewData.justification || 'No justification provided'}</div>
                  </div>

                  {/* Supporting Documents */}
                  {reviewData.documentPath && (
                    <div className="mb-3">
                      <label className="form-label small mb-2">Supporting Documents</label>
                      <div className="list-group list-group-sm">
                        <button
                          type="button"
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => {
                            setPreviewFile(reviewData.documentPath);
                            setPreviewFileName(reviewData.documentName || reviewData.documentPath || 'document');
                            setShowPreviewModal(true);
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <div>
                              <div className="small"><strong>{reviewData.documentName || reviewData.documentPath}</strong></div>
                              <div className="text-muted" style={{fontSize: '0.75rem'}}>Click to view</div>
                            </div>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  <hr className="my-3" />

                  {/* Review Summary */}
                    {reviewData.status !== 'pending' && reviewData.status !== 'need_revision' && reviewData.status !== 'revision_requested' && (
                      <div className="mb-3">
                        <label className="form-label small mb-2">Review Summary</label>
                        <div className="p-3 border rounded small bg-light">
                          <div><strong>Status:</strong> {mapStatusDisplay(reviewData.status)}</div>
                        <div><strong>Reviewed By:</strong> {reviewData.reviewedBy || '-'}</div>
                        <div><strong>Review Date:</strong> {formatDate(reviewData.reviewDate) || '-'}</div>
                      </div>
                    </div>
                  )}

                  {/* Review Decision */}
                  <div className="mb-3">
                    <label className="form-label small mb-2">
                      Review Decision {reviewMode === 'edit' && <span className="text-danger">*</span>}
                    </label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="decision"
                          id="decision_approve"
                          value="approve"
                          checked={reviewDecision === 'approve'}
                          onChange={(e) => setReviewDecision(e.target.value)}
                          disabled={reviewMode === 'view'}
                        />
                        <label className="form-check-label small" htmlFor="decision_approve">Approve Request</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="decision"
                          id="decision_revision"
                          value="revision"
                          checked={reviewDecision === 'revision'}
                          onChange={(e) => setReviewDecision(e.target.value)}
                          disabled={reviewMode === 'view'}
                        />
                        <label className="form-check-label small" htmlFor="decision_revision">Need Revision</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="decision"
                          id="decision_reject"
                          value="reject"
                          checked={reviewDecision === 'reject'}
                          onChange={(e) => setReviewDecision(e.target.value)}
                          disabled={reviewMode === 'view'}
                        />
                        <label className="form-check-label small" htmlFor="decision_reject">Reject</label>
                      </div>
                    </div>
                  </div>

                  {/* Review Notes */}
                  <div className="mb-3">
                    <label className="form-label small mb-2">
                      Review Notes {reviewMode === 'edit' && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={reviewNotes}
                      onChange={e => setReviewNotes(e.target.value)}
                      placeholder={reviewMode === 'view' ? 'No review notes provided' : 'Provide detailed notes on your decision'}
                      readOnly={reviewMode === 'view'}
                      required={reviewMode === 'edit'}
                    />
                  </div>

                  {/* Divider before History Log */}
                  <hr className="my-4" />

                  {/* History Log */}
                  <div className="mb-3">
                    <label className="form-label small mb-2">History Log</label>
                    <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                      <CompetencyHistory competencyId={reviewData.id} />
                    </div>
                  </div>
                </>
              );
            })()}
          </>
        ) : (
          <div className="text-muted text-center py-4">No request selected</div>
        )}
      </div>
            <div className="modal-footer border-top">
              <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">
                {reviewMode === 'view' ? 'Close' : 'Cancel'}
              </button>
              {reviewMode === 'edit' && (
                <button type="button" className="btn btn-primary" onClick={handleReviewDecision}>Submit Review</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      <div className={`modal modal-blur fade ${showPreviewModal ? 'show' : ''}`} style={{display: showPreviewModal ? 'block' : 'none'}} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Document Preview: {previewFileName}</h5>
              <button type="button" className="btn-close" onClick={() => setShowPreviewModal(false)}></button>
            </div>
            <div className="modal-body" style={{maxHeight: '70vh', overflow: 'auto'}}>
              {previewFile && (
                <>
                  {previewFile.startsWith('data:application/pdf') || previewFile.toLowerCase().includes('.pdf') ? (
                    <iframe 
                      src={previewFile} 
                      style={{width: '100%', height: '600px', border: 'none'}}
                      title="PDF Preview"
                    />
                  ) : previewFile.startsWith('data:image/') || previewFile.toLowerCase().match(/\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/) ? (
                    <img 
                      src={previewFile} 
                      alt="Document Preview" 
                      style={{maxWidth: '100%', height: 'auto'}}
                    />
                  ) : previewFile.startsWith('data:text/') || previewFile.toLowerCase().match(/\.(txt|csv|log)(\?|#|$)/) ? (
                    <iframe 
                      src={previewFile} 
                      style={{width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: '4px'}}
                      title="Text Preview"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted mb-3">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <p className="text-muted">Preview not available for this file type</p>
                      <button type="button" className="btn btn-primary" onClick={handleDownloadPreview}>
                        Download File
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleDownloadPreview}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setShowPreviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {showPreviewModal && <div className="modal-backdrop fade show" onClick={() => setShowPreviewModal(false)}></div>}

      {/* Deactivate Competency Modal */}
      {deactivateTarget && (
        <>
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deactivate Competency</h5>
                  <button type="button" className="btn-close" onClick={closeDeactivateModal} disabled={deactivateLoading}></button>
                </div>
                <div className="modal-body">
                  <p className="text-secondary">
                    This will mark the competency as <strong>Inactive</strong> for{' '}
                    <strong>{getEmployeeDisplayNameById(deactivateTarget.employeeId)}</strong> ({deactivateTarget.standard}),
                    even though its status is currently <strong>{mapStatusDisplay(deactivateTarget.status)}</strong>.
                    It is not deleted and can be reactivated later.
                  </p>
                  <label className="form-label">
                    Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={deactivateNotes}
                    onChange={(e) => { setDeactivateNotes(e.target.value); setDeactivateError(''); }}
                    placeholder="Explain why this competency is being deactivated"
                    disabled={deactivateLoading}
                    required
                  />
                  {deactivateError && <div className="text-danger small mt-2">{deactivateError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link" onClick={closeDeactivateModal} disabled={deactivateLoading}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-warning" onClick={handleConfirmDeactivate} disabled={deactivateLoading}>
                    {deactivateLoading ? 'Deactivating...' : 'Deactivate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={closeDeactivateModal}></div>
        </>
      )}
    </>
  );
};

export default Competency;
